import React from "react"
import moment from "moment"
import {DatePicker} from "antd"
import {Icon} from "components/Icon"
import cx from "classnames"
import styles from "./BaseDatePicker.module.css"
import {useModel} from "hooks"
import {toDateOnlyMoment, toDateOnlyString} from "helpers"
import {getAntdDatePickerLocale} from "helpers/antdWeekStart"

type DatePickerComponentsProps = {[key in string]: any}

export type BaseDatePickerProps = DatePickerComponentsProps & {
    value?: any
    onChange?: (value: any, text: string) => void
    className?: string
    placeholder?: string
    format?: string
    disabled?: boolean
    readOnly?: boolean
    allowClear?: boolean
    error?: boolean
    dateOnly?: boolean
    isUtcDate?: boolean
    suffixIcon?: JSX.Element
    timezone?: string
    disabledDate?: (currentDate: any) => boolean
}

export function BaseDatePicker(props: BaseDatePickerProps) {
    const model = useModel()
    const {
        onChange,
        className,
        format,
        placeholder,
        error,
        readOnly,
        disabled,
        allowClear,
        dateOnly = false,
        isUtcDate = false,
        timezone,
        value,
        locale: localeProp,
        ...rest
    } = props
    const currentUserDateFormat = model && model.getUserDateFormat && model.getUserDateFormat()
    const startWeekOn = model?.getStartWeekOn?.() ?? 0
    const pickerLocale = localeProp || getAntdDatePickerLocale(startWeekOn)
    const pickerValue = dateOnly ? toDateOnlyMoment(value) : isUtcDate && value ? moment.utc(value) : value

    return (
        <DatePicker
            key={`datepicker-${startWeekOn}`}
            onChange={(value, dateString) => {
                if (!value) {
                    onChange?.(null, dateString)
                } else if (dateOnly) {
                    onChange?.(toDateOnlyString(value), dateString)
                } else if (isUtcDate) {
                    onChange?.(moment.utc(value.format("YYYY-MM-DD")), dateString)
                } else if (timezone) {
                    const data = moment(value).clone().tz(timezone).startOf("day").toISOString()
                    onChange?.(data, dateString)
                } else {
                    onChange?.(value, dateString)
                }
            }}
            className={cx(styles.datePicker, className, {
                [styles.error]: error,
                [styles.disabled]: disabled,
                [styles.readOnly]: readOnly
            })}
            suffixIcon={
                <div className={styles.calendarIconWrap}>
                    <Icon icon="CALENDAR_INPUT" color="#fff" />
                </div>
            }
            allowClear={allowClear}
            placeholder={placeholder || currentUserDateFormat || "MM/DD/YYYY"}
            format={format || currentUserDateFormat || "MM/DD/YYYY"}
            disabled={disabled || readOnly}
            value={pickerValue}
            {...rest}
            locale={pickerLocale}
        />
    )
}
